import { useCallback, useEffect, useMemo, useState } from "react";
import { FileSpreadsheet } from "lucide-react";

import Button from "../components/ui/Button";
import TimeRangePicker from "../components/ui/TimeRangePicker";
import ColumnPicker from "../components/ui/ColumnPicker";
import ProductsTable from "../components/products/ProductsTable";
import { GROUPS, HIDEABLE_COLUMN_KEYS } from "../components/products/columns";
import useAsyncData from "../hooks/useAsyncData";
import { getProductPerformance } from "../utils/api";
import { getPresetRange } from "../utils/dateRanges";
import { isoDay } from "../utils/format";
import { buildProductPerformanceCsv, downloadCsv } from "../utils/csv";

const NO_ASYNC_EXPANSION = { loading: new Set(), errors: {} };

/**
 * Roll the per-period rows the API returns into one row per destination, and
 * keep the periods as that row's children.
 *
 * Every total is summed from the same periods that render underneath it, so a
 * parent row and its children can never disagree — which they would if the
 * backend sent a separate aggregate.
 */
function groupByDestination(rows) {
  const byKey = new Map();

  for (const row of rows) {
    let group = byKey.get(row.key);
    if (!group) {
      group = {
        key: row.key,
        label: row.label,
        kind: row.kind,
        handle: row.handle,
        spend: 0,
        sessions: 0,
        paidSessions: 0,
        cartAdditions: 0,
        converted: 0,
        orders: 0,
        units: 0,
        revenue: 0,
        periods: [],
      };
      byKey.set(row.key, group);
    }
    group.spend += row.spend;
    group.sessions += row.sessions;
    group.paidSessions += row.paidSessions;
    group.cartAdditions += row.cartAdditions;
    group.converted += row.converted;
    group.orders += row.orders;
    group.units += row.units;
    group.revenue += row.revenue;
    group.periods.push(row);
  }

  // Recomputed from the totals rather than averaged across periods — a mean of
  // weekly rates weights a quiet week the same as a busy one.
  const ratio = (numerator, denominator) =>
    denominator ? numerator / denominator : null;

  return [...byKey.values()]
    .map((g) => ({
      ...g,
      periods: g.periods.sort((a, b) => a.period.localeCompare(b.period)),
      paidShare: ratio(g.paidSessions, g.sessions),
      atcRate: ratio(g.cartAdditions, g.sessions),
      pageCvr: ratio(g.converted, g.sessions),
      productCvr: ratio(g.orders, g.sessions),
      aov: ratio(g.revenue, g.orders),
      mer: ratio(g.revenue, g.spend),
      cpa: ratio(g.spend, g.orders),
      costPerSession: ratio(g.spend, g.paidSessions),
    }))
    .sort((a, b) => b.spend - a.spend || a.label.localeCompare(b.label));
}

export default function ProductsPage() {
  const [range, setRange] = useState({ preset: "90d", ...getPresetRange("90d") });

  const {
    data: table,
    loading,
    error,
  } = useAsyncData(
    useCallback(
      () => getProductPerformance({ start: range.start, end: range.end }),
      [range]
    ),
    [range],
    { errorMessage: "Failed to load product performance." }
  );

  const products = useMemo(
    () => (table ? groupByDestination(table.rows) : null),
    [table]
  );

  const [openRows, setOpenRows] = useState(() => new Set());
  const [hidden, setHidden] = useState(() => new Set());

  // A new range means new rows; anything open belongs to rows about to vanish.
  useEffect(() => {
    setOpenRows(new Set());
  }, [range]);

  const handleToggle = useCallback((key) => {
    // No fetch on the way open: the API already returned every period, so
    // expanding is pure presentation.
    setOpenRows((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleColumn = useCallback((key) => {
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const children = useMemo(() => {
    if (!products) return {};
    return Object.fromEntries(products.map((p) => [p.key, p.periods]));
  }, [products]);

  const totalSpend = (products || []).reduce((sum, p) => sum + p.spend, 0);
  const attributed = (products || [])
    .filter((p) => p.kind === "product")
    .reduce((sum, p) => sum + p.spend, 0);

  function handleExportCsv() {
    if (!table) return;
    downloadCsv(
      `product-performance-${isoDay(range.start)}-to-${isoDay(range.end)}.csv`,
      buildProductPerformanceCsv({ range, rows: table.rows })
    );
  }

  return (
    <div className="w-full flex flex-col gap-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-mono text-sm uppercase tracking-widest">Product Performance</h1>
          <p className="font-mono text-xs text-collectors-gray max-w-2xl leading-relaxed">
            What we spent driving traffic to each page, and what that traffic did. Spend
            is attributed by the destination URL on each ad&rsquo;s creative, so it is
            the money that actually pointed at this page rather than a guess from the ad
            name. Click any row to break it into weeks.
          </p>
        </div>
        <Button onClick={handleExportCsv} disabled={!products || products.length === 0}>
          <FileSpreadsheet size={16} className="inline mr-2" />
          Export CSV
        </Button>
      </div>

      <TimeRangePicker value={range} onChange={setRange} />

      <ColumnPicker
        groups={GROUPS}
        hidden={hidden}
        onToggleColumn={toggleColumn}
        onShowAll={() => setHidden(new Set())}
        onHideAll={() => setHidden(new Set(HIDEABLE_COLUMN_KEYS))}
      />

      {loading && (
        <p className="font-mono text-xs uppercase tracking-widest text-collectors-gray py-6">
          Loading&hellip; this one reads every ad&rsquo;s destination, so it takes a few seconds.
        </p>
      )}

      {error && (
        <p className="font-mono text-xs uppercase tracking-widest text-red-700 py-6">{error}</p>
      )}

      {!loading && !error && (
        <ProductsTable
          rows={products}
          hidden={hidden}
          expansion={{
            ...NO_ASYNC_EXPANSION,
            open: openRows,
            children,
            onToggle: handleToggle,
          }}
        />
      )}

      {!loading && !error && products && products.length > 0 && (
        <p className="font-mono text-[11px] text-collectors-gray leading-relaxed max-w-3xl">
          <span className="font-bold">
            {totalSpend ? Math.round((attributed / totalSpend) * 100) : 0}% of spend
          </span>{" "}
          in this range points at a product page. The rest points at collection pages, or
          at ads whose destination could not be read — both are kept as their own rows so
          the spend column still totals to the ad account.
          <br />
          <br />
          <span className="font-bold">Page CVR and Product CVR are not the same thing.</span>{" "}
          Page CVR is sessions landing here that bought anything, which is the honest
          measure of whether the traffic we buy for this page converts. Product CVR is
          orders containing this product over the same sessions, so a page can sell into
          orders that arrived somewhere else entirely. Where the two diverge, that gap is
          the finding.
          <br />
          <br />
          Destinations are read from each creative as it stands today and applied to the
          whole range, so editing an ad&rsquo;s link re-attributes its history. Meta keeps
          no record of what the link used to be.
        </p>
      )}
    </div>
  );
}
